import { AxiosResponse } from 'axios';
import cache from 'memory-cache';
import { HttpClient } from '../utils/http/http-client';
import { IPaginationOptions } from '@/infrastructure/interfaces/i-pagination-options';
import {
  IGetPaginatedRecitersResponse,
  IGetReciterResponse,
  IReciter,
} from '@/infrastructure/interfaces/i-reciter';
import {
  IGetPaginatedReadSurahsResponse,
  IGetReadSurahResponse,
  IReadSurah,
} from '@/infrastructure/interfaces/i-read-surah';
import {
  IAudioSurah,
  IGetAudioSurahResponse,
  IGetPaginatedAudioSurahsResponse,
} from '@/infrastructure/interfaces/i-audio-surah';
import { IAyat, IGetAyasResponse } from '@/infrastructure/interfaces/i-ayat';
import { IGetTranslationsResponse } from '@/infrastructure/interfaces/i-translation';
import { IGetPromotionalAppResponse } from '@/infrastructure/interfaces/i-promotional-app';
import { IContact } from '@/infrastructure/interfaces/i-contact';
import { SiteContentType } from '@/infrastructure/enum/site-content.enum';
import { IGetSiteContentResponse } from '@/infrastructure/interfaces/i-site-content';
import {
  IPublicBlogListParams,
  IPublicBlogListResponse,
  IPublicBlogPost,
} from '@/infrastructure/interfaces/i-blog';
import { getPublishedBlogPostEndpoint } from '@/infrastructure/blog/urls';
import { PublicBlogApiError } from '@/infrastructure/blog/errors';

const API_DOMAIN: string = process.env.API_DOMAIN || '';
const API_KEY: string = process.env.API_KEY || '';
const OUR_APPS_DOMAIN: string = process.env.OUR_APPS_DOMAIN || '';
const OUR_APPS_LINK: string = process.env.OUR_APPS_LINK || '';

export class Api extends HttpClient {
  private static classInstance?: Api;

  private constructor() {
    super(API_DOMAIN, API_KEY);
  }

  public checkCache = (url: string) => {
    return cache.get(url);
  };

  public cacheResponse = (key: string, data: any) => {
    const hours = 24;
    cache.put(key, data, hours * 1000 * 60 * 60);
    return data;
  };

  public cachedFetch = async <T>(
    key: string,
    url: string,
    params?: any,
    refetch = false
  ): Promise<AxiosResponse<T, any>> => {
    const cachedResponse = this.checkCache(key);
    if (cachedResponse && !refetch) {
      return cachedResponse;
    } else {
      try {
        const response = await this.instance.get<T>(url, {
          params,
        });

        this.cacheResponse(key, response);
        return response;
      } catch (error) {
        throw error;
      }
    }
  };

  public static getInstance() {
    if (!this.classInstance) {
      this.classInstance = new Api();
    }

    return this.classInstance;
  }

  public getReciters = async (
    options?: IPaginationOptions<IReciter>,
    selectFields: (keyof IReciter)[] = [],
    refetch = false
  ): Promise<AxiosResponse<IGetPaginatedRecitersResponse, any>> => {
    let key = `getReciters`;
    const url = `/web/reciters/all`;
    const params: IPaginationOptions<IReciter> = {
      limit: options?.limit || 0,
      page: options?.page || 0,
      random: options?.random || false,
    };
    if (params.limit) {
      key += `-${params.limit}`;
    }
    if (params.page) {
      key += `-${params.page}`;
    }
    if (params.random) {
      key += `-${params.random}`;
    }
    if (selectFields.length) {
      params.select = selectFields;
      key += `-${selectFields.join('-')}`;
    }

    return await this.cachedFetch<IGetPaginatedRecitersResponse>(
      key,
      url,
      params,
      refetch
    );
  };
  public getNextReciters = async (
    id: number,
    options?: IPaginationOptions<IReciter>,
    selectFields: (keyof IReciter)[] = [],
    refetch = false
  ): Promise<AxiosResponse<IGetPaginatedRecitersResponse, any>> => {
    let key = `getNextReciters-${id}`;
    const url = `/web/reciters/next/${id}`;

    const params: IPaginationOptions<IReciter> = {
      limit: options?.limit || 0,
    };
    if (params.limit) {
      key += `-${params.limit}`;
    }
    if (selectFields.length) {
      params.select = selectFields;
      key += `-${selectFields.join('-')}`;
    }

    return await this.cachedFetch<IGetPaginatedRecitersResponse>(
      key,
      url,
      params,
      refetch
    );
  };

  public getReciterBySlug = async (
    slug: string,
    refetch = false
  ): Promise<AxiosResponse<IGetReciterResponse, any>> => {
    const key = `getReciterBySlug-${slug}`;
    const url = `/web/reciters/slug/${slug}`;

    return await this.cachedFetch<IGetReciterResponse>(key, url, {}, refetch);
  };

  public getReciterByGuid = async (
    guid: string,
    refetch = false
  ): Promise<AxiosResponse<IGetReciterResponse, any>> => {
    const key = `getReciterByGuid-${guid}`;
    const url = `/web/reciters/guid/${guid}`;

    return await this.cachedFetch<IGetReciterResponse>(key, url, {}, refetch);
  };

  public getRecitersByGuid = async (
    guids: string[],
    refetch = false
  ): Promise<AxiosResponse<IReciter[], any>> => {
    const queue = guids.map(async (guid) => {
      const { data } = await this.getReciterByGuid(guid, refetch);
      return data.reciter;
    });
    return Promise.all(queue);
  };

  public getFeaturedReciters = async (
    options?: IPaginationOptions<IReciter>,
    selectFields: (keyof IReciter)[] = [],
    refetch = false
  ): Promise<AxiosResponse<IGetPaginatedRecitersResponse, any>> => {
    let key = `getFeaturedReciters`;
    const url = `/web/reciters/featured`;
    const params: IPaginationOptions<IReciter> = {
      limit: options?.limit || 0,
      page: options?.page || 0,
      random: options?.random || false,
    };
    if (params.limit) {
      key += `-${params.limit}`;
    }
    if (params.page) {
      key += `-${params.page}`;
    }
    if (params.random) {
      key += `-${params.random}`;
    }
    if (selectFields.length) {
      params.select = selectFields;
      key += `-${selectFields.join('-')}`;
    }

    return await this.cachedFetch<IGetPaginatedRecitersResponse>(
      key,
      url,
      params,
      refetch
    );
  };

  public getPopularReciters = async (
    options?: IPaginationOptions<IReciter>,
    selectFields: (keyof IReciter)[] = [],
    refetch = false
  ): Promise<AxiosResponse<IGetPaginatedRecitersResponse, any>> => {
    let key = `getPopularReciters`;
    const url = `/web/reciters/popular`;
    const params: IPaginationOptions<IReciter> = {
      limit: options?.limit || 0,
      page: options?.page || 0,
      random: options?.random || false,
    };
    if (params.limit) {
      key += `-${params.limit}`;
    }
    if (params.page) {
      key += `-${params.page}`;
    }
    if (params.random) {
      key += `-${params.random}`;
    }
    if (selectFields.length) {
      params.select = selectFields;
      key += `-${selectFields.join('-')}`;
    }

    return await this.cachedFetch<IGetPaginatedRecitersResponse>(
      key,
      url,
      params,
      refetch
    );
  };

  public getReadSurahBySlug = async (
    slug: string,
    refetch = false
  ): Promise<AxiosResponse<IGetReadSurahResponse, any>> => {
    const key = `getReadSurahBySlug-${slug}`;
    const url = `/web/surahs/slug/${slug}`;

    return await this.cachedFetch<IGetPaginatedRecitersResponse>(
      key,
      url,
      {},
      refetch
    );
  };

  public getReadSurahBySurahNumber = async (
    number: number,
    refetch = false
  ): Promise<AxiosResponse<IGetReadSurahResponse, any>> => {
    const key = `getReadSurahBySurahNumber-${number}`;
    const url = `/web/surahs/index/${number}`;

    return await this.cachedFetch<IGetPaginatedRecitersResponse>(
      key,
      url,
      {},
      refetch
    );
  };

  public getAyasBySuraId = async (
    suraId: string,
    translation = '',
    refetch = false
  ): Promise<AxiosResponse<IGetAyasResponse, any>> => {
    const key = `getAyasBySuraId-${suraId}-${translation}`;
    const url = `web/ayas/sura/${suraId}`;

    const params = {
      translation,
    };

    return await this.cachedFetch<IGetPaginatedRecitersResponse>(
      key,
      url,
      params,
      refetch
    );
  };

  public getAllTranslations = async (
    refetch = false
  ): Promise<AxiosResponse<IGetTranslationsResponse, any>> => {
    const key = `getAllTranslations`;
    const url = `/web/translations`;

    return await this.cachedFetch<IGetPaginatedRecitersResponse>(
      key,
      url,
      {},
      refetch
    );
  };

  public getReadSurahs = async (
    options?: IPaginationOptions<IReadSurah>,
    selectFields: (keyof IReadSurah)[] = [],
    refetch = false
  ): Promise<AxiosResponse<IGetPaginatedReadSurahsResponse, any>> => {
    let key = `getReadSurahs`;
    const url = `/web/surahs/all`;
    const params: IPaginationOptions<IReadSurah> = {
      limit: options?.limit || 0,
      page: options?.page || 0,
      random: options?.random || false,
    };
    if (params.limit) {
      key += `-${params.limit}`;
    }
    if (params.page) {
      key += `-${params.page}`;
    }
    if (params.random) {
      key += `-${params.random}`;
    }
    if (selectFields.length) {
      params.select = selectFields;
      key += `-${selectFields.join('-')}`;
    }

    return await this.cachedFetch<IGetPaginatedRecitersResponse>(
      key,
      url,
      params,
      refetch
    );
  };

  public getAudioSurahsByReciterSlug = async (
    reciterSlug: string,
    options?: IPaginationOptions<IAudioSurah[]>,
    selectFields: (keyof IAudioSurah)[] = [],
    refetch = false
  ): Promise<AxiosResponse<IGetPaginatedAudioSurahsResponse, any>> => {
    let key = `getAudioSurahsByReciterSlug-${reciterSlug}`;
    const url = `/web/audio-surahs/reciter/${reciterSlug}`;
    const params: IPaginationOptions<IAudioSurah> = {
      limit: options?.limit || 0,
      page: options?.page || 0,
      random: options?.random || false,
    };
    if (params.limit) {
      key += `-${params.limit}`;
    }
    if (params.page) {
      key += `-${params.page}`;
    }
    if (params.random) {
      key += `-${params.random}`;
    }
    if (selectFields.length) {
      params.select = selectFields;
      key += `-${selectFields.join('-')}`;
    }

    return await this.cachedFetch<IGetPaginatedRecitersResponse>(
      key,
      url,
      params,
      refetch
    );
  };

  public getAudioSurahsBySurahNumber = async (
    surahNumber: number,
    options?: IPaginationOptions<IAudioSurah[]>,
    selectFields: (keyof IAudioSurah)[] = [],
    refetch = false
  ): Promise<AxiosResponse<IGetPaginatedAudioSurahsResponse, any>> => {
    let key = `getAudioSurahsBySurahNumber-${surahNumber}`;
    const url = `/web/audio-surahs/surah/${surahNumber}`;
    const params: IPaginationOptions<IAudioSurah> = {
      limit: options?.limit || 0,
      page: options?.page || 0,
      random: options?.random || false,
    };
    if (params.limit) {
      key += `-${params.limit}`;
    }
    if (params.page) {
      key += `-${params.page}`;
    }
    if (params.random) {
      key += `-${params.random}`;
    }
    if (selectFields.length) {
      params.select = selectFields;
      key += `-${selectFields.join('-')}`;
    }

    return await this.cachedFetch<IGetPaginatedRecitersResponse>(
      key,
      url,
      params,
      refetch
    );
  };

  public getAudioSurahBySlug = async (
    slug: string,
    refetch = false
  ): Promise<AxiosResponse<IGetAudioSurahResponse, any>> => {
    const key = `getAudioSurahsBySlug-${slug}`;
    const url = `/web/audio-surahs/slug/${slug}`;

    return await this.cachedFetch<IGetAudioSurahResponse>(
      key,
      url,
      {},
      refetch
    );
  };

  public getAudioSurahByGuid = async (
    guid: string,
    refetch = false
  ): Promise<AxiosResponse<IGetAudioSurahResponse, any>> => {
    const key = `getAudioSurahByGuid-${guid}`;
    const url = `/web/audio-surahs/guid/${guid}`;

    return await this.cachedFetch<IGetAudioSurahResponse>(
      key,
      url,
      {},
      refetch
    );
  };

  public getPromotionalApps = async (
    refetch = false
  ): Promise<AxiosResponse<IGetPromotionalAppResponse, any>> => {
    const key = `getPromotionalApps`;
    const url = `${OUR_APPS_DOMAIN}${OUR_APPS_LINK}`;

    return await this.cachedFetch<IGetPromotionalAppResponse>(
      key,
      url,
      {},
      refetch
    );
  };

  public searchReciters = async (
    searchvalue: string,
    options?: IPaginationOptions<IReciter>,
    refetch = false
  ): Promise<AxiosResponse<IGetPaginatedRecitersResponse, any>> => {
    let key = `searchReciters-${searchvalue}`;
    const url = `/search/reciters?search=${searchvalue}`;

    const params: IPaginationOptions<IReciter> = {
      limit: options?.limit || 0,
      page: options?.page || 0,
      random: options?.random || false,
    };
    if (params.limit) {
      key += `-${params.limit}`;
    }
    if (params.page) {
      key += `-${params.page}`;
    }
    if (params.random) {
      key += `-${params.random}`;
    }

    return await this.cachedFetch<IGetPaginatedRecitersResponse>(
      key,
      url,
      params,
      refetch
    );
  };

  public searchReadSurahs = async (
    searchvalue: string,
    options?: IPaginationOptions<IReadSurah>,
    refetch = false
  ): Promise<AxiosResponse<IGetPaginatedReadSurahsResponse, any>> => {
    let key = `searchReadSurahs-${searchvalue}`;
    const url = `/search/surahs?search=${searchvalue}`;

    const params: IPaginationOptions<IReadSurah> = {
      limit: options?.limit || 0,
      page: options?.page || 0,
      random: options?.random || false,
    };
    if (params.limit) {
      key += `-${params.limit}`;
    }
    if (params.page) {
      key += `-${params.page}`;
    }
    if (params.random) {
      key += `-${params.random}`;
    }

    return await this.cachedFetch<IGetPaginatedRecitersResponse>(
      key,
      url,
      params,
      refetch
    );
  };

  public searchAyas = async (
    searchvalue: string,
    options?: IPaginationOptions<IAyat>,
    refetch = false
  ): Promise<AxiosResponse<IGetAyasResponse, any>> => {
    let key = `searchAyas-${searchvalue}`;
    const url = `/search/ayas?search=${searchvalue}`;

    const params: IPaginationOptions<IReadSurah> = {
      limit: options?.limit || 0,
      page: options?.page || 0,
      random: options?.random || false,
    };
    if (params.limit) {
      key += `-${params.limit}`;
    }
    if (params.page) {
      key += `-${params.page}`;
    }
    if (params.random) {
      key += `-${params.random}`;
    }

    return await this.cachedFetch<IGetPaginatedRecitersResponse>(
      key,
      url,
      params,
      refetch
    );
  };

  public sendContactForm = async (data: IContact) =>
    this.instance.post('/api/email', data);

  public getSiteContentByType = async (type: SiteContentType) => {
    const key = `getSiteContentByType-${type}`;
    const url = `/web/site-content/type/${type}`;
    return await this.cachedFetch<IGetSiteContentResponse>(key, url);
  };

  public getPublishedBlogPosts = async (
    params: IPublicBlogListParams = {}
  ): Promise<IPublicBlogListResponse> => {
    const query = {
      page: params.page || 1,
      limit: params.limit || 20,
      ...(params.language ? { language: params.language } : {}),
    };
    const key = `getPublishedBlogPosts-${query.page}-${query.limit}-${
      params.language || 'all'
    }`;
    try {
      const response = (await this.cachedFetch<IPublicBlogListResponse>(
        key,
        '/web/blog/posts',
        query
      )) as unknown as AxiosResponse<IPublicBlogListResponse>;
      return response.data;
    } catch (error) {
      const status = (error as { response?: { status?: number } }).response
        ?.status;
      throw new PublicBlogApiError('list', status);
    }
  };

  public getPublishedBlogPost = async (
    slug: string
  ): Promise<IPublicBlogPost> => {
    try {
      const response = (await this.cachedFetch<IPublicBlogPost>(
        `getPublishedBlogPost-${slug}`,
        getPublishedBlogPostEndpoint(slug)
      )) as unknown as AxiosResponse<IPublicBlogPost>;
      return response.data;
    } catch (error) {
      const status = (error as { response?: { status?: number } }).response
        ?.status;
      throw new PublicBlogApiError('detail', status);
    }
  };
}
